/**
 * WHAT: SQL Server (MSSQL) user access report with connection-scoped filtering and privilege detail modals.
 * WHY:  MSSQL DBAs need to review individual user privileges per SQL Server instance, including
 *       server-level vs database-level access and disabled account status, to satisfy attestation requirements.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_CONFIG from '../config/api.config';
import { useSafeTimeout } from '../hooks';
import { WideModal } from './common';
import './MySQLUserAccessReport.css'; // Reuse MySQL styles

/**
 * WHAT: Deterministically map a database name to a distinct chip color (bg/text/border).
 * WHY:  A single SQL Server instance can host databases whose names differ by one
 *       character (e.g. `dama-mssql` vs `dama_mssql`). Rendered in the same flat blue,
 *       they read as accidental duplicates. Hashing the exact name into a fixed palette
 *       gives each distinct name its own stable color — same name → same color every
 *       render, look-alike names → visibly different colors — so they're trivial to tell
 *       apart at a glance without adding extra columns or badges.
 */
const DB_CHIP_PALETTE = [
  { bg: '#e0e7ff', fg: '#3730a3', bd: '#a5b4fc' }, // indigo
  { bg: '#dcfce7', fg: '#166534', bd: '#86efac' }, // green
  { bg: '#fef3c7', fg: '#92400e', bd: '#fbbf24' }, // amber
  { bg: '#fce7f3', fg: '#9d174d', bd: '#f9a8d4' }, // pink
  { bg: '#cffafe', fg: '#155e75', bd: '#67e8f9' }, // cyan
  { bg: '#ede9fe', fg: '#5b21b6', bd: '#c4b5fd' }, // violet
  { bg: '#ffedd5', fg: '#9a3412', bd: '#fdba74' }, // orange
  { bg: '#d1fae5', fg: '#065f46', bd: '#6ee7b7' }, // emerald
];

const getDbChipColor = (name) => {
  // Hash every character so single-char differences land on different buckets.
  const s = String(name || '');
  let hash = 0;
  for (let i = 0; i < s.length; i++) {
    hash = (hash * 31 + s.charCodeAt(i)) | 0;
  }
  return DB_CHIP_PALETTE[Math.abs(hash) % DB_CHIP_PALETTE.length];
};

/**
 * WHAT: Renders a per-connection MSSQL user access report with filters for privilege level, status, and search.
 * WHY:  Allows MSSQL administrators and compliance reviewers to examine user-level access grants
 *       on each SQL Server connection, supporting risk assessment and attestation decision-making.
 */
const MSSQLUserAccessReport = () => {
  const [connections, setConnections] = useState([]);
  const [users, setUsers] = useState([]);
  const [statistics, setStatistics] = useState({});
  const [databaseInfo, setDatabaseInfo] = useState({});
  const [selectedConnection, setSelectedConnection] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [collectionDates, setCollectionDates] = useState([]);
  const [filters, setFilters] = useState({
    collection_date: '',
    privilege_level: '',
    is_server_principal: '',
    is_disabled: '',
    user_type: '', // Internal | Individual | Application (blank = all)
    physical_database: '', // specific in-scope physical DB (blank = all)
    search: ''
  });

  // Physical-DB dropdown options. One MSSQL connection collects from many
  // in-scope databases; the backend returns the distinct physical DB names
  // present in the data so the "Physical Database" filter lists only real
  // options. Server logins (instance-wide) are unaffected by this filter.
  const [availablePhysicalDbs, setAvailablePhysicalDbs] = useState([]);

  // Pagination — server-side. Backend returns total_count/total_pages for
  // the current filter set; the client tracks current page + size only.
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailModalData, setDetailModalData] = useState({ title: '', items: [], type: '' });

  // Privilege-level breakdown drill-down. Clicking a principal's Privilege
  // Level opens a WideModal showing the components (direct permissions + role
  // memberships, each at its own level) that produced the level via MAX, then
  // matrix-clamp — the same rules MSSQL Stage 4 categorization applies, so the
  // breakdown agrees with the stored level shown in the row.
  const [levelBreakdown, setLevelBreakdown] = useState(null);
  // Risk-level drill-down: the 4-step CIS/NIST pipeline (privilege → dormancy
  // skipped (MSSQL has no last_login) → status → sysadmin/securityadmin) that
  // produced the user's risk tier.
  const [riskBreakdown, setRiskBreakdown] = useState(null);

  // Privilege-level filter options driven by the Privilege Matrix per-tech config.
  // Disabling SUPER_ADMIN for MSSQL in the matrix removes it from this dropdown.
  const [activeTiers, setActiveTiers] = useState([]);

  // Certification status for the MSSQL privilege mappings. If any privilege or
  // fixed role is UNASSIGNED (level < 0) or uncertified, the computed user
  // privilege levels in this report may be UNDERSTATED — an unassigned/
  // uncertified mapping contributes nothing to a user's level until a DBA
  // classifies it. We surface that as a top-of-report warning so a reviewer
  // does not attest against potentially incomplete numbers.
  const [certStatus, setCertStatus] = useState(null);

  // Safe timeout hook for delayed operations
  const { setSafeTimeout } = useSafeTimeout();

  useEffect(() => {
    fetchMSSQLConnections();
    fetchActiveTiers();
    fetchCertificationStatus();
  }, []);

  // Reuses the same endpoint ManagePrivilegeMappings uses for its banner, so
  // the numbers here are consistent with that screen. Best-effort: a failure
  // just hides the warning rather than blocking the report.
  const fetchCertificationStatus = async () => {
    try {
      const response = await axios.get(
        API_CONFIG.getDatabasesEndpoint('privilege-reference/check-certification/'),
        { params: { database_type: 'MSSQL' } }
      );
      if (response.data?.status === 'success') {
        setCertStatus(response.data);
      }
    } catch (e) {
      // Non-fatal: the report is still usable; we just omit the warning.
      setCertStatus(null);
    }
  };

  const fetchActiveTiers = async () => {
    try {
      const response = await axios.get(API_CONFIG.getDatabasesEndpoint('privilege-matrix/mssql/active-tiers/'));
      if (response.data?.success) {
        setActiveTiers(response.data.tiers || []);
      }
    } catch (err) {
      console.error('Failed to fetch active tiers for MSSQL:', err);
    }
  };

  const fetchMSSQLConnections = async () => {
    try {
      console.log('Fetching MSSQL connections...');
      const response = await axios.get(API_CONFIG.getDatabasesEndpoint('mssql/source-connections/'));
      console.log('MSSQL connections:', response.data.connections);
      const mssqlConns = response.data.connections || [];
      // Map id to connection_id for consistency
      const mappedConns = mssqlConns.map(conn => ({
        ...conn,
        connection_id: conn.id,
        database_type: 'MSSQL'
      }));
      console.log('MSSQL connections found:', mappedConns);
      setConnections(mappedConns);
      if (mappedConns.length > 0 && !selectedConnection) {
        console.log('Setting selected connection to:', mappedConns[0].connection_id);
        setSelectedConnection(mappedConns[0].connection_id);
        fetchUsers(mappedConns[0].connection_id);
      }
    } catch (err) {
      console.error('Error fetching SQL Server connections:', err);
      setError('Failed to fetch SQL Server connections');
    }
  };

  const fetchCollectionDates = async (connId = '') => {
    if (!connId) { setCollectionDates([]); return; }
    try {
      const response = await axios.get(API_CONFIG.getDatabasesEndpoint(`mssql-access/collection-dates/?connection_id=${connId}`));
      setCollectionDates(response.data.collection_dates || []);
    } catch (err) {
      console.error('Error fetching collection dates:', err);
      setCollectionDates([]);
    }
  };

  const fetchUsers = async (connId = selectedConnection, overridePage = null, overrideSize = null, overrideFilters = null) => {
    if (!connId) return;

    setLoading(true);
    setError(null);
    try {
      const p = overridePage ?? currentPage;
      const s = overrideSize ?? pageSize;
      // Prefer explicit overrideFilters so callers driven by an onChange event
      // can send the just-typed value WITHOUT waiting for setFilters to commit.
      // setState is async — reading `filters` here would lose the keystroke that
      // triggered this fetch.
      const f = overrideFilters || filters;
      const response = await axios.post(API_CONFIG.getDatabasesEndpoint('mssql-access/report/'), {
        connection_id: connId,
        page: p,
        page_size: s,
        filters: {
          ...(f.collection_date && { collection_date: f.collection_date }),
          ...(f.privilege_level && { privilege_level: parseInt(f.privilege_level) }),
          ...(f.is_server_principal !== '' && { is_server_principal: f.is_server_principal === 'true' }),
          ...(f.is_disabled !== '' && { is_disabled: f.is_disabled === 'true' }),
          ...(f.user_type && { user_type: f.user_type }),
          ...(f.physical_database && { physical_database: f.physical_database }),
          ...(f.search && { search: f.search })
        }
      });
      setUsers(response.data.users || []);
      setStatistics(response.data.statistics || {});
      setDatabaseInfo(response.data.database_info || {});
      setTotalCount(response.data.total_count || 0);
      setTotalPages(response.data.total_pages || 0);
      // Independent of the current filter set, so the dropdown options stay
      // stable as the user switches between DBs.
      setAvailablePhysicalDbs(response.data.available_physical_databases || []);
    } catch (err) {
      // Clear the report so a failed load (e.g. an uncollected DB) shows an
      // empty report — NOT the previously-selected database's stale rows.
      setUsers([]);
      setStatistics({});
      setDatabaseInfo({});
      setTotalCount(0);
      setTotalPages(0);
      setError(err.response?.data?.error || 'Failed to fetch user access report');
      console.error('Error fetching SQL Server users:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleConnectionChange = (e) => {
    const connId = e.target.value;
    setSelectedConnection(connId);
    setFilters(prev => ({ ...prev, collection_date: '' }));
    setCurrentPage(1);
    fetchCollectionDates(connId);
    fetchUsers(connId, 1, pageSize);
  };

  // Debounce timer for free-text search input — typed input must NOT fire a
  // request on every keystroke. Dropdown changes apply immediately.
  const searchDebounceTimer = React.useRef(null);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    const nextFilters = { ...filters, [name]: value };
    setFilters(nextFilters);
    setCurrentPage(1);

    if (name === 'search') {
      // Debounce so we don't fire on every keystroke.
      if (searchDebounceTimer.current) clearTimeout(searchDebounceTimer.current);
      searchDebounceTimer.current = setSafeTimeout(
        () => fetchUsers(selectedConnection, 1, pageSize, nextFilters),
        400
      );
    } else {
      // Auto-apply dropdowns immediately. Pass nextFilters explicitly because
      // setFilters above hasn't committed yet.
      fetchUsers(selectedConnection, 1, pageSize, nextFilters);
    }
  };

  const applyFilters = () => {
    // Kept for the Enter-key handler on the search input — flushes any pending
    // debounce immediately.
    if (searchDebounceTimer.current) clearTimeout(searchDebounceTimer.current);
    setCurrentPage(1);
    fetchUsers(selectedConnection, 1, pageSize, filters);
  };

  const resetFilters = () => {
    const cleared = { collection_date: '', privilege_level: '', is_server_principal: '', is_disabled: '', user_type: '', physical_database: '', search: '' };
    setFilters(cleared);
    setCollectionDates([]);
    setCurrentPage(1);
    fetchUsers(selectedConnection, 1, pageSize, cleared);
  };

  const exportToCSV = async () => {
    try {
      const response = await axios.post(
        API_CONFIG.getDatabasesEndpoint('mssql-access/export/csv/'),
        { connection_id: selectedConnection, filters },
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().split('T')[0];
      link.setAttribute('download', `sqlserver_access_report_${timestamp}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Error exporting CSV:', err);
    }
  };

  // Server-side pagination: render whatever the server returned for this page.
  // startIndex/endIndex are display-only values for "Showing X-Y of N" copy.
  const currentUsers = users;
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + users.length, totalCount);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages && newPage !== currentPage) {
      setCurrentPage(newPage);
      fetchUsers(selectedConnection, newPage, pageSize);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(1);
    fetchUsers(selectedConnection, 1, newSize);
  };

  const getRiskBadgeClass = (level) => {
    if (level >= 6) return 'text-danger fw-bold';
    if (level >= 4) return 'text-warning fw-bold';
    if (level >= 2) return 'text-info';
    return 'text-secondary';
  };

  // Risk-tier (LOW/MEDIUM/HIGH/CRITICAL string) → text class. Used by the
  // server-provided risk_level (which can be lifted to CRITICAL by sysadmin
  // membership independently of the privilege level).
  const getRiskTierBadgeClass = (tier) => {
    switch (tier) {
      case 'CRITICAL': return 'text-danger fw-bold';
      case 'HIGH': return 'text-warning fw-bold';
      case 'MEDIUM': return 'text-info';
      case 'LOW': return 'text-secondary';
      default: return 'text-muted';
    }
  };

  const getPrincipalTypeLabel = (isServerPrincipal) => {
    return isServerPrincipal ? 'Server Login' : 'Database User';
  };

  // Shared pagination strip — rendered both above and below the table so users
  // can navigate without scrolling. `placement` only differentiates the border
  // (top of table = bottom border, bottom of table = top border).
  const renderPaginationBar = (placement) => {
    if (totalCount === 0) return null;
    const borderStyle = placement === 'top'
      ? { borderBottom: '1px solid #e5e7eb', marginBottom: '10px' }
      : { borderTop: '1px solid #e5e7eb', marginTop: '20px' };
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '15px',
        ...borderStyle,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <label>Rows per page:</label>
          <select value={pageSize} onChange={(e) => handlePageSizeChange(Number(e.target.value))}>
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <span style={{ marginLeft: '20px' }}>
            Showing {startIndex + 1}-{endIndex} of {totalCount}
          </span>
        </div>

        <div style={{ display: 'flex', gap: '5px' }}>
          <button className="btn btn-secondary btn-small" onClick={() => handlePageChange(1)} disabled={currentPage === 1}>First</button>
          <button className="btn btn-secondary btn-small" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>Previous</button>
          <span style={{ padding: '0 10px', alignSelf: 'center' }}>Page {currentPage} of {totalPages}</span>
          <button className="btn btn-secondary btn-small" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>Next</button>
          <button className="btn btn-secondary btn-small" onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages}>Last</button>
        </div>
      </div>
    );
  };

  const showPermissionDetails = (user, type) => {
    let title = '';
    let items = [];

    if (type === 'server') {
      title = `Server Permissions for ${user.principal_name}`;
      items = user.server_permissions || [];
    } else if (type === 'database') {
      title = `Database Permissions for ${user.principal_name}`;
      items = user.database_permissions || [];
    } else if (type === 'table') {
      title = `Table/Object Permissions for ${user.principal_name}`;
      items = user.table_permissions || [];
    }

    setDetailModalData({ title, items, type: 'permission' });
    setShowDetailModal(true);
  };

  const showRoleDetails = (user, type) => {
    let title = '';
    let items = [];

    if (type === 'server') {
      title = `Server Roles for ${user.principal_name}`;
      items = user.server_roles || [];
    } else if (type === 'database') {
      title = `Database Roles for ${user.principal_name}`;
      items = user.database_roles || [];
    }

    setDetailModalData({ title, items, type: 'role' });
    setShowDetailModal(true);
  };

  // Fetch and open the privilege-level breakdown for one principal. Scoped by
  // connection + principal + server/db scope + physical database so the
  // backend resolves the EXACT row this report rendered (a database user can
  // recur across in-scope DBs; a server login is instance-wide).
  const openLevelBreakdown = async (user) => {
    setLevelBreakdown({ name: user.principal_name, loading: true, data: null });
    try {
      const resp = await axios.get(
        API_CONFIG.getDatabasesEndpoint('privilege-reference/mssql-user-component-levels/'),
        { params: {
          database_type: 'MSSQL',
          connection_id: selectedConnection,
          principal_name: user.principal_name,
          is_server_principal: user.is_server_principal ? 'true' : 'false',
          database_name: user.is_server_principal ? undefined : (user.database_name || undefined),
        } }
      );
      setLevelBreakdown({ name: user.principal_name, loading: false, data: resp.data });
    } catch (e) {
      setLevelBreakdown({ name: user.principal_name, loading: false, data: null, error: e.response?.data?.message || e.message });
    }
  };

  // Fetch the 4-step Risk Level breakdown for a principal. Uses the same
  // identifying tuple as the privilege-level breakdown so the trace is over
  // the exact same source row the report rendered.
  const openRiskBreakdown = async (user) => {
    setRiskBreakdown({ name: user.principal_name, loading: true, data: null });
    try {
      const resp = await axios.get(
        API_CONFIG.getDatabasesEndpoint('privilege-reference/mssql-user-risk-breakdown/'),
        { params: {
          database_type: 'MSSQL',
          connection_id: selectedConnection,
          principal_name: user.principal_name,
          is_server_principal: user.is_server_principal ? 'true' : 'false',
          database_name: user.is_server_principal ? undefined : (user.database_name || undefined),
        } }
      );
      setRiskBreakdown({ name: user.principal_name, loading: false, data: resp.data });
    } catch (e) {
      setRiskBreakdown({ name: user.principal_name, loading: false, data: null, error: e.response?.data?.message || e.message });
    }
  };

  return (
    <div className="mysql-access-report">
      <div className="report-header" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px' }}>
        <div>
          <h2>SQL Server User Access Report</h2>
          <p className="report-subtitle">Comprehensive principal and permission analysis</p>
        </div>
        <button className="btn btn-export" onClick={exportToCSV} style={{ flexShrink: 0 }}>📊 Export to CSV</button>
      </div>

      {/* Privilege-mapping completeness warning. Shown when any MSSQL
          privilege or fixed role is UNASSIGNED or uncertified — in that case
          those mappings contribute nothing to a user's computed level, so the
          privilege levels below may be understated. Derived entirely from the
          shared check-certification endpoint (same source as the Manage
          Privilege Mappings banner). */}
      {certStatus && (() => {
        const unassigned = (certStatus.unassigned_count || 0);
        const uncertPriv = (certStatus.uncertified_count || 0);
        const uncertRole = (certStatus.role_uncertified_count || 0);
        const incomplete = unassigned + uncertPriv + uncertRole;
        if (incomplete === 0) return null;
        return (
          <div style={{
            background: '#fff3cd',
            border: '2px solid #ffc107',
            borderRadius: '12px',
            padding: '16px 20px',
            marginBottom: '20px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
          }}>
            <div style={{ color: '#856404', fontSize: '0.95rem', lineHeight: 1.5 }}>
              <i className="fas fa-exclamation-triangle" style={{ marginRight: '8px' }}></i>
              <strong>Privilege levels below may be understated.</strong>
              {' '}The MSSQL privilege mappings are incomplete:
              {unassigned > 0 && <> <strong>{unassigned}</strong> unassigned privilege{unassigned === 1 ? '' : 's'}</>}
              {unassigned > 0 && (uncertPriv > 0 || uncertRole > 0) ? ',' : ''}
              {uncertPriv > 0 && <> <strong>{uncertPriv}</strong> uncertified privilege{uncertPriv === 1 ? '' : 's'}</>}
              {uncertPriv > 0 && uncertRole > 0 ? ',' : ''}
              {uncertRole > 0 && <> <strong>{uncertRole}</strong> uncertified role{uncertRole === 1 ? '' : 's'}</>}.
              {' '}Unassigned or uncertified mappings do not contribute to a user's
              computed privilege level until a DBA classifies and certifies them in{' '}
              <strong>Manage Privilege Mappings</strong>, so the levels shown here
              may be lower than a user's effective access.
            </div>
          </div>
        );
      })()}

      {/* Compact summary cards — scoped override only for this report so the
          shared .summary-card (used by MySQL/Oracle/etc.) is untouched.
          Matches the slim density of the Privilege Mappings workspace. */}
      <style>{`
        .mssql-compact-cards {
          display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 16px;
        }
        /* Narrower min so more cards fit on one line; flex-grow keeps them
           filling the row evenly when there's space. */
        .mssql-compact-cards .summary-card {
          flex: 1 1 96px; padding: 6px 10px; border-radius: 7px;
          box-shadow: none; transition: none;
          display: flex; align-items: baseline; justify-content: space-between;
          gap: 8px;
        }
        .mssql-compact-cards .summary-card:hover { transform: none; box-shadow: none; }
        .mssql-compact-cards .summary-label {
          font-size: 10.5px; margin-bottom: 0; white-space: nowrap;
        }
        .mssql-compact-cards .summary-value { font-size: 17px; }
      `}</style>
      {(() => {
        // Dynamic privilege-level cards. One card per DISTINCT privilege level
        // actually present (count > 0) in this report's privilege_distribution
        // (computed by the backend over the full collection). Ordered by
        // severity high→low so the riskiest tiers read first. Levels with no
        // users are omitted, so the card set changes with the data. The four
        // context cards (Total/Server/Database/Disabled) stay fixed.
        const SEVERITY_ORDER = [
          ['SUPER_ADMIN', 'danger'],
          ['ADMIN', 'danger'],
          ['WRITE_ELEVATED', 'warning'],
          ['WRITE', 'warning'],
          ['READ_ELEVATED', 'info'],
          ['READ', 'info'],
          ['NONE', ''],
          ['UNASSIGNED', ''],
        ];
        const dist = statistics.privilege_distribution || {};
        const levelCards = SEVERITY_ORDER
          .filter(([name]) => (dist[name] || 0) > 0)
          .map(([name, cls]) => ({ name, cls, count: dist[name] }));
        return (
          <div className="summary-cards mssql-compact-cards">
            <div className="summary-card">
              <div className="summary-label">Total Principals</div>
              <div className="summary-value">{statistics.total_principals || 0}</div>
            </div>
            <div className="summary-card info">
              <div className="summary-label">Server Logins</div>
              <div className="summary-value">{statistics.server_logins || 0}</div>
            </div>
            <div className="summary-card info">
              <div className="summary-label">Database Users</div>
              <div className="summary-value">{statistics.database_users || 0}</div>
            </div>
            {levelCards.map(c => (
              <div key={c.name} className={`summary-card ${c.cls}`}>
                <div className="summary-label">{c.name.replace(/_/g, ' ')}</div>
                <div className="summary-value">{c.count}</div>
              </div>
            ))}
            <div className="summary-card">
              <div className="summary-label">Disabled Accounts</div>
              <div className="summary-value">{statistics.disabled_accounts || 0}</div>
            </div>
          </div>
        );
      })()}

      {/* Filters */}
      <div className="filters-section">
        <h3>Filters</h3>
        <div className="filters-grid">
          <div className="filter-group">
            <label>SQL Server Instance</label>
            <select value={selectedConnection} onChange={handleConnectionChange}>
              <option value="">Select Connection</option>
              {connections.map(conn => (
                <option key={conn.connection_id} value={conn.connection_id}>
                  {conn.database_name || conn.connection_name} - {conn.host || conn.db_host}:{conn.port || conn.db_port}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Collection Date</label>
            <select name="collection_date" value={filters.collection_date} onChange={handleFilterChange}>
              <option value="">Latest</option>
              {collectionDates.map((cd, idx) => (
                <option key={idx} value={cd.value}>{cd.label}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Privilege Level</label>
            <select name="privilege_level" value={filters.privilege_level} onChange={handleFilterChange}>
              <option value="">All Levels</option>
              {activeTiers.map(t => (
                <option key={t.tier_name} value={t.tier_value}>{t.tier_name}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Principal Type</label>
            <select name="is_server_principal" value={filters.is_server_principal} onChange={handleFilterChange}>
              <option value="">All Types</option>
              <option value="true">Server Logins</option>
              <option value="false">Database Users</option>
            </select>
          </div>

          {/* Physical Database: one MSSQL connection spans many in-scope DBs.
              Selecting one narrows to that DB's users plus all server logins
              (instance-wide). Only shown when the data has >1 physical DB. */}
          {availablePhysicalDbs.length > 1 && (
            <div className="filter-group">
              <label>Physical Database</label>
              <select name="physical_database" value={filters.physical_database} onChange={handleFilterChange}>
                <option value="">All Databases</option>
                {availablePhysicalDbs.map(db => (
                  <option key={db} value={db}>{db}</option>
                ))}
              </select>
            </div>
          )}

          <div className="filter-group">
            <label>Account Status</label>
            <select name="is_disabled" value={filters.is_disabled} onChange={handleFilterChange}>
              <option value="">All Statuses</option>
              <option value="false">Enabled</option>
              <option value="true">Disabled</option>
            </select>
          </div>

          <div className="filter-group">
            <label>User Type</label>
            <select name="user_type" value={filters.user_type} onChange={handleFilterChange}>
              <option value="">All Users</option>
              <option value="Internal">Internal</option>
              <option value="Individual">Individual</option>
              <option value="Security Group">Security Group</option>
              <option value="Application Service Account">Application Service Account</option>
              <option value="Unclassified">Unclassified</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Principal Name Search</label>
            <input
              type="text"
              name="search"
              value={filters.search}
              onChange={handleFilterChange}
              placeholder="Search principal name..."
              onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
            />
          </div>

          <div className="filter-actions">
            <button className="btn btn-secondary" onClick={resetFilters}>Reset</button>
          </div>
        </div>
      </div>

      {error && (
        <div className="error-message" style={{ whiteSpace: 'normal', overflow: 'visible', maxWidth: 'none' }}>
          <span style={{ flex: 1 }}>{error}</span>
          <button onClick={() => setError(null)}>✕</button>
        </div>
      )}

      {/* Users Table */}
      {loading ? (
        <div className="loading">Loading...</div>
      ) : (
        <div className="users-table-container">
          <h3>
            Principal Access Details
            {totalCount > 0
              ? ` (Showing ${startIndex + 1}-${endIndex} of ${totalCount} principals)`
              : ` (0 principals)`}
          </h3>
          {renderPaginationBar('top')}
          <div className="table-wrapper">
            <table className="table table-hover mb-0 users-table">
              <thead className="table-dark">
                <tr>
                  <th>Principal Name</th>
                  <th>Database</th>
                  <th>User Type</th>
                  <th>Principal Type</th>
                  <th>Status</th>
                  <th>Server Permissions</th>
                  <th>Database Permissions</th>
                  <th>Table Permissions</th>
                  <th>Server Roles</th>
                  <th>Database Roles</th>
                  <th>Privilege Level</th>
                  <th>Risk Assessment</th>
                </tr>
              </thead>
              <tbody>
                {currentUsers.map((user, index) => {
                  const isHighRisk = user.privilege_level >= 5;
                  return (
                    <tr key={index} className={isHighRisk ? 'critical-row' : ''}>
                      <td className="username-cell">
                        {user.principal_name}
                        {user.is_group && (
                          <span style={{
                            marginLeft: '8px',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontSize: '10px',
                            fontWeight: '600',
                            backgroundColor: '#fef3c7',
                            color: '#92400e',
                            border: '1px solid #fbbf24'
                          }}>
                            GROUP
                          </span>
                        )}
                      </td>
                      <td>
                        {user.accessible_databases && user.accessible_databases.length > 0 ? (
                          <div style={{ fontSize: '11px' }}>
                            {user.accessible_databases.length <= 3 ? (
                              // Show all databases if 3 or fewer. Each DB name gets its
                              // own deterministic color so look-alike names (e.g.
                              // dama-mssql vs dama_mssql) are easy to tell apart.
                              user.accessible_databases.map((db, idx) => {
                                const c = getDbChipColor(db);
                                return (
                                  <span key={idx} style={{
                                    display: 'inline-block',
                                    margin: '2px',
                                    padding: '2px 6px',
                                    borderRadius: '3px',
                                    backgroundColor: c.bg,
                                    color: c.fg,
                                    border: `1px solid ${c.bd}`,
                                    fontWeight: '500'
                                  }}>
                                    {db}
                                  </span>
                                );
                              })
                            ) : (
                              // Show count with tooltip if more than 3
                              <span
                                style={{
                                  padding: '2px 6px',
                                  borderRadius: '3px',
                                  backgroundColor: '#dbeafe',
                                  color: '#1e40af',
                                  fontWeight: '500',
                                  cursor: 'help'
                                }}
                                title={user.accessible_databases.join(', ')}
                              >
                                {user.accessible_databases.length} databases
                              </span>
                            )}
                          </div>
                        ) : (
                          <span style={{ color: '#9ca3af', fontSize: '12px' }}>None</span>
                        )}
                      </td>
                      <td>
                        {user.user_type === 'Internal' ? (
                          <span className="text-secondary" title="System-managed by the database engine"><i className="fas fa-cog fa-xs me-1"></i>Internal</span>
                        ) : user.user_type === 'Individual' ? (
                          <span className="text-success" title="Named user matched in enterprise directory"><i className="fas fa-user fa-xs me-1"></i>Individual</span>
                        ) : user.user_type === 'Security Group' ? (
                          <span className="text-warning" title="Role or group principal (not a real user)"><i className="fas fa-users fa-xs me-1"></i>Security Group</span>
                        ) : user.user_type === 'Application Service Account' ? (
                          <span className="text-info" title="Service account registered in account_owner with a designated attester"><i className="fas fa-cube fa-xs me-1"></i>App Service</span>
                        ) : user.user_type === 'Unclassified' ? (
                          <span className="text-danger" title="Not in any registry — register in database_owners or account_owner"><i className="fas fa-question-circle fa-xs me-1"></i>Unclassified</span>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td>
                        <span style={{ fontSize: '12px', color: user.is_server_principal ? '#2563eb' : '#059669' }}>
                          {getPrincipalTypeLabel(user.is_server_principal)}
                        </span>
                      </td>
                      <td>
                        <span style={{
                          color: user.is_disabled ? '#dc2626' : '#059669',
                          fontWeight: user.is_disabled ? 'bold' : 'normal'
                        }}>
                          {user.is_disabled ? 'DISABLED' : 'ENABLED'}
                        </span>
                      </td>
                      <td>
                        {(user.direct_server_permission_count || 0) > 0 ? (
                          <a href="#" onClick={(e) => { e.preventDefault(); showPermissionDetails(user, 'server'); }}
                             style={{ color: '#2563eb', textDecoration: 'underline', cursor: 'pointer' }}>
                            {user.direct_server_permission_count || 0}
                          </a>
                        ) : (
                          <span style={{ color: '#9ca3af' }}>0</span>
                        )}
                      </td>
                      <td>
                        {(user.direct_database_permission_count || 0) > 0 ? (
                          <a href="#" onClick={(e) => { e.preventDefault(); showPermissionDetails(user, 'database'); }}
                             style={{ color: '#2563eb', textDecoration: 'underline', cursor: 'pointer' }}>
                            {user.direct_database_permission_count || 0}
                          </a>
                        ) : (
                          <span style={{ color: '#9ca3af' }}>0</span>
                        )}
                      </td>
                      <td>
                        {(user.table_permission_count || 0) > 0 ? (
                          <a href="#" onClick={(e) => { e.preventDefault(); showPermissionDetails(user, 'table'); }}
                             style={{ color: '#7c3aed', textDecoration: 'underline', cursor: 'pointer' }}>
                            {user.table_permission_count || 0}
                          </a>
                        ) : (
                          <span style={{ color: '#9ca3af' }}>0</span>
                        )}
                      </td>
                      <td>
                        {(user.server_role_count || 0) > 0 ? (
                          <a href="#" onClick={(e) => { e.preventDefault(); showRoleDetails(user, 'server'); }}
                             style={{ color: '#dc2626', textDecoration: 'underline', cursor: 'pointer' }}>
                            {user.server_role_count || 0}
                          </a>
                        ) : (
                          <span style={{ color: '#9ca3af' }}>0</span>
                        )}
                      </td>
                      <td>
                        {(user.database_role_count || 0) > 0 ? (
                          <a href="#" onClick={(e) => { e.preventDefault(); showRoleDetails(user, 'database'); }}
                             style={{ color: '#059669', textDecoration: 'underline', cursor: 'pointer' }}>
                            {user.database_role_count || 0}
                          </a>
                        ) : (
                          <span style={{ color: '#9ca3af' }}>0</span>
                        )}
                      </td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-link p-0 border-0 align-baseline"
                          style={{ textDecoration: 'none' }}
                          title="Show how this level was derived"
                          onClick={() => openLevelBreakdown(user)}
                        >
                          <span className={getRiskBadgeClass(user.privilege_level)}>
                            {user.privilege_level_name || 'NONE'}
                          </span>
                          <i className="fas fa-external-link-alt ms-1" style={{ fontSize: '9px', color: '#6b7280' }}></i>
                        </button>
                      </td>
                      <td>
                        {(() => {
                          // Server-provided risk_level wins (includes sysadmin → CRITICAL).
                          // Fall back to deriving from privilege_level only if missing.
                          const tier = user.risk_level || (
                            user.privilege_level >= 6 ? 'CRITICAL' :
                            user.privilege_level >= 4 ? 'HIGH' :
                            user.privilege_level >= 2 ? 'MEDIUM' : 'LOW'
                          );
                          return (
                            <button
                              type="button"
                              className="btn btn-link p-0 border-0 align-baseline"
                              style={{ textDecoration: 'none' }}
                              title="Show how this risk level was calculated"
                              onClick={() => openRiskBreakdown(user)}
                            >
                              <span className={getRiskTierBadgeClass(tier)}>{tier}</span>
                              <i className="fas fa-external-link-alt ms-1" style={{ fontSize: '9px', color: '#6b7280' }}></i>
                            </button>
                          );
                        })()}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {renderPaginationBar('bottom')}
        </div>
      )}

      {/* Detail Modal */}
      <WideModal
        show={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        title={detailModalData.title}
        headerIcon="fa-key"
        maxWidth="800px"
        footer={<button className="btn btn-secondary" onClick={() => setShowDetailModal(false)}>Close</button>}
      >
        {detailModalData.items.length === 0 ? (
          <p style={{ color: '#6b7280', textAlign: 'center', padding: '20px' }}>
            No {detailModalData.type === 'permission' ? 'permissions' : 'roles'} found.
          </p>
        ) : (
          <div style={{ maxHeight: '60vh', overflow: 'auto' }}>
            {detailModalData.type === 'permission' ? (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f3f4f6' }}>
                    <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Permission</th>
                    <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>State</th>
                    <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Scope</th>
                    <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Object</th>
                  </tr>
                </thead>
                <tbody>
                  {detailModalData.items.map((perm, idx) => (
                    <tr key={idx} style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '8px', fontWeight: '500' }}>{perm.permission_name || perm}</td>
                      <td style={{ padding: '8px' }}>
                        <span style={{
                          padding: '2px 6px',
                          borderRadius: '4px',
                          fontSize: '11px',
                          backgroundColor: perm.state === 'GRANT' ? '#d1fae5' : perm.state === 'DENY' ? '#fee2e2' : '#fef3c7',
                          color: perm.state === 'GRANT' ? '#065f46' : perm.state === 'DENY' ? '#991b1b' : '#92400e'
                        }}>
                          {perm.state || 'GRANT'}
                        </span>
                      </td>
                      <td style={{ padding: '8px', color: '#6b7280' }}>{perm.scope || 'N/A'}</td>
                      <td style={{ padding: '8px', color: '#6b7280', fontSize: '12px' }}>{perm.object_name || 'N/A'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f3f4f6' }}>
                    <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Role Name</th>
                    <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Type</th>
                  </tr>
                </thead>
                <tbody>
                  {detailModalData.items.map((role, idx) => (
                    <tr key={idx} style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '8px', fontWeight: '500' }}>{role.role_name || role}</td>
                      <td style={{ padding: '8px' }}>
                        <span style={{
                          padding: '2px 6px',
                          borderRadius: '4px',
                          fontSize: '11px',
                          backgroundColor: role.is_fixed_role ? '#dbeafe' : '#f3f4f6',
                          color: role.is_fixed_role ? '#1e40af' : '#374151'
                        }}>
                          {role.is_fixed_role ? 'Fixed Role' : 'Custom Role'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </WideModal>

      {/* Privilege-level breakdown — the direct permissions and role
          memberships that produced this principal's level (MAX rule, then
          matrix-clamped). Mirrors the same derivation MSSQL Stage 4
          categorization applies, so it agrees with the level in the row. */}
      <WideModal
        show={!!levelBreakdown}
        onClose={() => setLevelBreakdown(null)}
        title={`Privilege Level — ${levelBreakdown?.name || ''}`}
        headerIcon="fa-calculator"
        headerClass="bg-dark text-white"
        maxWidth="900px"
        footer={<button className="btn btn-secondary" onClick={() => setLevelBreakdown(null)}>Close</button>}
      >
        {levelBreakdown?.loading && (
          <div style={{ textAlign: 'center', padding: '24px' }}>
            <i className="fas fa-spinner fa-spin" style={{ marginRight: '8px' }}></i>Loading breakdown…
          </div>
        )}
        {levelBreakdown?.error && (
          <div className="error-message">{levelBreakdown.error}</div>
        )}
        {levelBreakdown?.data && (() => {
          const d = levelBreakdown.data;
          const order = { SUPER_ADMIN: 6, ADMIN: 5, WRITE_ELEVATED: 4, WRITE: 3, READ_ELEVATED: 2, READ: 1, NONE: 0, UNASSIGNED: -1 };
          // Collapse to DISTINCT (component, kind, scope, level, OBJECT, STATE).
          // §K62: the dedup key includes object_name and effective_state so a
          // per-object BLOCKED/DENY row is NOT collapsed into a GRANT of the same
          // (component,kind,scope,level) on a different object — that would hide
          // the deny. A deduped row drives the level if ANY occurrence does.
          const dedupMap = new Map();
          for (const c of (d.components || [])) {
            const key = `${c.component}|${c.kind}|${c.scope}|${c.level_name}|${c.object_name || ''}|${c.effective_state || 'GRANT'}`;
            const existing = dedupMap.get(key);
            if (existing) {
              existing.is_driver = existing.is_driver || c.is_driver;
            } else {
              dedupMap.set(key, { ...c });
            }
          }
          const rows = [...dedupMap.values()].sort((a, b) => (order[b.level_name] ?? 0) - (order[a.level_name] ?? 0));
          const stale = d.stored_level_name && d.stored_level_name !== d.derived_level_name;
          const scopeLabel = d.is_server_principal ? 'Server login (instance-wide)' : `Database user · ${d.database_name || 'N/A'}`;
          const cnt = d.counts || {};
          return (
            <>
              {stale && (
                <div style={{
                  background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '8px',
                  padding: '10px 14px', marginBottom: '14px', color: '#856404', fontSize: '0.9rem'
                }}>
                  <i className="fas fa-triangle-exclamation" style={{ marginRight: '8px' }}></i>
                  Stored level is <strong>{d.stored_level_name}</strong> but a live recompute gives{' '}
                  <strong>{d.derived_level_name}</strong>. Re-run the data refresh to reconcile.
                </div>
              )}
              <div style={{
                display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '14px',
                background: '#f8f9fa', borderRadius: '6px', padding: '10px 14px', marginBottom: '16px'
              }}>
                <span className={getRiskBadgeClass(d.derived_level)} style={{ fontSize: '1.05rem' }}>
                  {d.derived_level_name}
                </span>
                <span style={{ color: '#6b7280', fontSize: '0.9rem' }}>
                  Level = <strong>MAX</strong> of every direct permission and role membership below
                  {d.matrix_capped
                    ? <> — raw <strong>{d.raw_max_level_name}</strong> was clamped to the matrix cap (<strong>{d.matrix_cap_name}</strong>).</>
                    : <>.</>}
                </span>
                <span style={{ marginLeft: 'auto', color: '#9ca3af', fontSize: '0.8rem' }}>
                  {scopeLabel} · {cnt.server_perms || 0} svr-perm · {cnt.database_perms || 0} db-perm · {(cnt.server_roles || 0) + (cnt.database_roles || 0)} role(s)
                </span>
              </div>
              {rows.length === 0 ? (
                <p style={{ color: '#6b7280', padding: '12px' }}>
                  No direct permissions or role memberships contribute to this level.
                </p>
              ) : (
                <table className="table table-sm table-hover mb-0">
                  <thead className="table-dark">
                    <tr>
                      <th>Component</th>
                      <th>Type</th>
                      <th>Scope</th>
                      <th>Level</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((c, i) => {
                      // §K62 3-state: BLOCKED = a grant cancelled by a DENY (shown,
                      // greyed + struck, not counted); DENY = the deny row itself.
                      const st = c.effective_state || 'GRANT';
                      const isBlocked = st === 'BLOCKED';
                      const isDeny = st === 'DENY';
                      const rowStyle = isBlocked
                        ? { background: '#f3f4f6', color: '#9ca3af' }
                        : isDeny
                          ? { background: '#fef2f2' }
                          : (c.is_driver ? { background: '#fff8e1' } : undefined);
                      return (
                      <tr key={i} style={rowStyle}>
                        <td style={{ fontWeight: 600, textDecoration: isBlocked ? 'line-through' : undefined }}>
                          {c.component}{c.object_name ? <span style={{ color: '#9ca3af', fontWeight: 400 }}> · {c.object_name}</span> : null}
                        </td>
                        <td>{c.kind}</td>
                        <td><span style={{ color: '#6b7280', fontSize: '12px' }}>{c.scope}</span></td>
                        <td><span className={getRiskBadgeClass(c.level)} style={isBlocked ? { opacity: 0.5 } : undefined}>{c.level_name}</span></td>
                        <td style={{ fontSize: '12px' }}>
                          {isDeny && <span style={{ color: '#dc2626', fontWeight: 600 }}><i className="fas fa-ban" style={{ marginRight: '4px' }}></i>DENY</span>}
                          {isBlocked && <span style={{ color: '#6b7280', fontWeight: 600 }}><i className="fas fa-ban" style={{ marginRight: '4px' }}></i>blocked by DENY (not counted)</span>}
                          {c.is_driver && <span style={{ color: '#d97706', fontWeight: 600 }}><i className="fas fa-bolt" style={{ marginRight: '4px' }}></i>drives level</span>}
                          {c.unassigned && <span style={{ color: '#92400e', marginLeft: '8px' }}><i className="fas fa-exclamation-circle" style={{ marginRight: '4px' }}></i>unassigned</span>}
                          {c.unknown && <span style={{ color: '#9ca3af', marginLeft: '8px' }}><i className="fas fa-question-circle" style={{ marginRight: '4px' }}></i>unknown</span>}
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </>
          );
        })()}
      </WideModal>

      {/* Risk Level breakdown — the 4-step CIS/NIST pipeline (privilege →
          dormancy (skipped for MSSQL — no last_login) → status → sysadmin /
          securityadmin boosters). Uses the same shared verbose calculator as
          the report's Python risk calc so the trace matches the badge. */}
      <WideModal
        show={!!riskBreakdown}
        onClose={() => setRiskBreakdown(null)}
        title={`Risk Level — ${riskBreakdown?.name || ''}`}
        headerIcon="fa-shield-halved"
        headerClass="bg-dark text-white"
        footer={
          <button type="button" className="btn btn-secondary" onClick={() => setRiskBreakdown(null)}>
            Close
          </button>
        }
      >
        {riskBreakdown?.loading && (
          <div className="text-center p-4"><i className="fas fa-spinner fa-spin me-2"></i>Loading breakdown…</div>
        )}
        {riskBreakdown?.error && (
          <div className="alert alert-danger">{riskBreakdown.error}</div>
        )}
        {riskBreakdown?.data && (() => {
          const d = riskBreakdown.data;
          const ins = d.inputs || {};
          const scopeLabel = d.is_server_principal ? 'Server login' : (d.database_name || 'Database user');
          return (
            <>
              <div className="d-flex align-items-center flex-wrap gap-3 mb-3 p-2" style={{ background: '#f8f9fa', borderRadius: 6 }}>
                <span className={getRiskTierBadgeClass(d.final_risk)} style={{ fontSize: '1.1em' }}>
                  <i className="fas fa-circle fa-xs me-1"></i>{d.final_risk}
                </span>
                <span className="text-muted small">
                  Risk = <strong>base</strong> from privilege level, then <strong>dormancy</strong>, <strong>status</strong>, and <strong>tech-specific boosters</strong> (clamped LOW..CRITICAL).
                </span>
                <span className="ms-auto text-muted small">
                  {scopeLabel}
                </span>
              </div>

              <table className="table table-sm table-bordered mb-3">
                <thead className="table-dark">
                  <tr>
                    <th style={{ width: '5%' }}>#</th>
                    <th style={{ width: '25%' }}>Factor</th>
                    <th style={{ width: '40%' }}>Input</th>
                    <th style={{ width: '15%' }}>Effect</th>
                    <th style={{ width: '15%' }}>Running risk</th>
                  </tr>
                </thead>
                <tbody>
                  {(d.steps || []).map((s, i) => (
                    <tr key={i}>
                      <td className="text-muted">{s.step}</td>
                      <td><strong>{s.name}</strong></td>
                      <td>
                        <code style={{ fontSize: '0.85em', color: '#0f172a' }}>
                          {JSON.stringify(s.input || {}, null, 0).replace(/[{}"]/g, '').replace(/,/g, ', ')}
                        </code>
                        {s.note && (
                          <div className="text-warning small mt-1">
                            <i className="fas fa-info-circle me-1"></i>{s.note}
                          </div>
                        )}
                      </td>
                      <td className="text-muted">{s.effect}</td>
                      <td>
                        <span className={getRiskTierBadgeClass(s.running_risk)}>
                          <i className="fas fa-circle fa-xs me-1"></i>{s.running_risk}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="card border-0" style={{ background: '#f8f9fa' }}>
                <div className="card-body py-2 small">
                  <div className="text-muted mb-1"><strong>Inputs used by this calculation:</strong></div>
                  <div className="row">
                    <div className="col-md-6">
                      <div>Privilege level: <code>{ins.privilege_level_name} ({ins.privilege_level})</code></div>
                      <div>Account status: <code>{ins.account_status}</code> <span className="text-muted">(is_disabled = {String(!!ins.is_disabled)})</span></div>
                    </div>
                    <div className="col-md-6">
                      <div>Is sysadmin: <code>{String(!!ins.is_sysadmin)}</code></div>
                      <div>Is securityadmin: <code>{String(!!ins.is_securityadmin)}</code></div>
                    </div>
                  </div>
                  <div className="text-muted mt-1" style={{ fontSize: '0.8em' }}>
                    <i className="fas fa-info-circle me-1"></i>MSSQL has no standard <code>last_login</code> column, so the dormancy step is a no-op.
                  </div>
                </div>
              </div>
            </>
          );
        })()}
      </WideModal>
    </div>
  );
};

export default MSSQLUserAccessReport;
